import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useState } from 'react';
import { Object3D } from 'three';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { useSnapshot } from 'valtio';

import defaultVerticalOffset from '@cartwhl/3d/gltf/defaultVerticalOffsets';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import { HAND_OPTIONS } from '@cartwhl/common/types';

import useCanvasRecorder from 'hooks/useCanvasRecorder';
import { useTransformFilesToAnimationData } from 'hooks/useLoadAnimation';
import { animationPlayerStore, projectStore, sceneSettingsStore } from 'stores';

import ActiveCharacter from './ActiveCharacter';

export default function StageFromBVHNew() {
  const {
    cameraSettings: { cameraFollow, serializedCamera },
    characterSettings: { staticHands, enableRootMotion, charUploadID },
  } = useSnapshot(sceneSettingsStore.state);

  const {
    currentProject,
    motion: { combinedBvhFile },
  } = useSnapshot(projectStore.state);

  const { trimFrameStart, trimFrameEnd } = useSnapshot(
    animationPlayerStore.state,
  );

  const { motion } = useSnapshot(projectStore.state);

  const characterInstanceID = Object.keys(
    currentProject.data?.characters || {},
  )[0];
  const characterData = currentProject.data?.characters?.[characterInstanceID];

  const hasTimelineItems = Object.values(motion.timelineData ?? {}).length > 0;
  const yOffset = hasTimelineItems ? 0 : defaultVerticalOffset(charUploadID);

  const bvhFiles = useMemo(
    () => (combinedBvhFile ? [combinedBvhFile] : null),
    [combinedBvhFile],
  );
  const transformedAnimationData = useTransformFilesToAnimationData(bvhFiles, {
    allowRootTranslation: true,
    staticHands: staticHands as HAND_OPTIONS,
    enableRootMotion: [enableRootMotion, enableRootMotion, true],
    trimFrameStart,
    trimFrameEnd,
    yOffset,
    rotationOffsets: characterData?.rotationOffsets,
  });

  if (!bvhFiles || !transformedAnimationData?.[0]) return null;

  return (
    <Stage
      bvhFile={transformedAnimationData[0].animationData}
      cameraFollow={cameraFollow}
      serializedCamera={serializedCamera}
    />
  );
}

function Stage({
  bvhFile,
  cameraFollow,
  serializedCamera,
}: {
  bvhFile: BVH;
  cameraFollow: boolean;
  serializedCamera: string;
}) {
  const [characterScene, setCharacterScene] = useState<Object3D | null>(null);

  const { autoPlayOnLoad, isRecording, disabled } = useSnapshot(
    animationPlayerStore.state,
  );

  const {
    backgroundSettings: {
      canvasHeight,
      canvasWidth,
      useBackgroundGradient,
      hexColor,
    },
    playbackSettings: { frameRate, playbackSpeed, frameStepSize },
  } = useSnapshot(sceneSettingsStore.state);

  const mixer = useFrameMixer(bvhFile.clip, characterScene ?? undefined, {
    frameRate,
    frameStepSize,
    playbackSpeed,
    autoplay: autoPlayOnLoad,
    disabled,
    onReady: () => animationPlayerStore.setMixerReady(true),
    onPlayChange: animationPlayerStore.setIsPlaying,
    onFrameUpdate: (frame) => {
      if (!isRecording) animationPlayerStore.setCurrentFrame(frame);
    },
  });

  useEffect(() => {
    if (mixer) {
      animationPlayerStore.setDuration({
        ...mixer.duration,
        originalFrames: mixer.duration.frames, /// This is not applicable with our rebuild, because we get trimmed BVH files, instead of trimming it ourselves
        originalSeconds: mixer.duration.seconds, /// This is not applicable with our rebuild, because we get trimmed BVH files, instead of trimming it ourselves
      });
      animationPlayerStore.hoistAnimationMixer(mixer);
    }
  }, [mixer, mixer.duration]);

  const canvasRecorder = useCanvasRecorder(mixer, {
    width: canvasWidth,
    height: canvasHeight,
    frameRate,
    playbackSpeed,
    backgroundColor: useBackgroundGradient ? undefined : hexColor,
  });

  useEffect(() => {
    if (canvasRecorder) {
      animationPlayerStore.setCanvasRecorder(canvasRecorder);
    }
  }, [canvasRecorder]);

  useFrame((state, delta) => {
    if (!isRecording) {
      mixer.update(delta);
    }
    animationPlayerStore.updateCamera({
      cameraFollow,
      delta,
      serializedCamera,
      target: state.scene.getObjectByName('pelvis'),
    });
  });

  useEffect(() => {
    projectStore.setMeshLoadingStatus('pending');
  }, []);

  const handleCharacterLoading = (loading: boolean) => {
    projectStore.setMeshLoadingStatus(loading ? 'pending' : 'ready');
  };

  return (
    <ActiveCharacter
      onSceneReady={setCharacterScene}
      onCharacterLoading={handleCharacterLoading}
    />
  );
}
